import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import * as SiteFeedbackAPI from '../../services/SiteFeedbackServices';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { FaCopy, FaUserSlash, FaUserPlus, FaMinus} from 'react-icons/fa'; 
import SiteFeedbackModal from './SiteFeedbackModal';
import socket from '../../socket';
import styles from './styles/SendSiteFeedback.module.css'
import Loading from '../loading/Loading';

const SendSiteFeedback = ({ isAdmin }) => {
    const navigate = useNavigate();
    const { userId, user } = useContext(AuthContext);

    const [text, setText] = useState('');
    const [allFeedback, setAllFeedback] = useState([]);

    const [feedbackModal, setFeedbackModal] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);

    const [feedbackAccess, setFeedbackAccess] = useState(null);
    
    const emitUpdate = (eventName, payload) => {
        if (isAdmin && socket) {
            socket.emit(eventName, payload);
        }
    }

    const selectedFeedbackRef = useRef(selectedFeedback);

    useEffect(() => {
        selectedFeedbackRef.current = selectedFeedback;
    }, [selectedFeedback]);

    const handleSubmit = async () => {
        const data = await SiteFeedbackAPI.createFeedback({ sender: userId, text, sitewide: true });

        socket.emit('createFeedback', {sender: userId, isAdmin, feedback: data});
        setAllFeedback(prev => [data, ...prev])
    }

    const handleNewFeedback = useCallback((data) => {
        setAllFeedback(prev => [data.feedback, ...prev])
    }, [setAllFeedback]) 

    const getFeedbackUser = async () => {
        try {
            const feedbacks = await SiteFeedbackAPI.getUserFeedback(userId);
            setAllFeedback(feedbacks);
        } catch (error) {
            console.error("Error fetching site feedback:", error);
        }
    }

    const handleNewMessage = useCallback((data) => {
        setAllFeedback(prev => (
                prev.map(obj =>
                    obj._id === data.feedbackId
                    ? { ...obj, messages: data.feedback.messages, updatedAt: data.feedback.updatedAt }
                    : obj
                )
            )
        );
        if(selectedFeedbackRef.current && selectedFeedbackRef.current._id === data.feedbackId){
            setSelectedFeedback(prev => ({ ...prev, messages: data.feedback.messages, updatedAt: data.feedback.updatedAt}));
        }
    }, [setAllFeedback, setSelectedFeedback])
    
    const getAllFeedback = async () => {
        try {
            const feedbacks = await SiteFeedbackAPI.getAllSiteFeedback();
            setAllFeedback(feedbacks);
        } catch (error) {
            console.error("Error fetching site feedback:", error);
        }
    }

    const statusSiteFeedback = async (feedbackId, status) => {
        try {
            const feedbacks = await SiteFeedbackAPI.setFeedbackStatus(feedbackId, status);
            setAllFeedback(prev => (
                prev.map(obj =>
                    obj._id === feedbackId
                    ? { ...obj, isClosed: status, updatedAt: feedbacks.updatedAt }
                    : obj
                )
            )
        );
        
        if(selectedFeedbackRef.current && selectedFeedbackRef.current._id === feedbackId){
            setSelectedFeedback(null);
            setFeedbackModal(false);
        }
            socket.emit('statusFeedback', { userId, isAdmin, feedbackId, status, feedbackOwner: feedbacks.sender._id, feedbacks });

            
        } catch (error) {
            console.error("Error fetching site feedback:", error);
        }
    }

    const handleFeedbackStatus = useCallback((data) => {
        setAllFeedback(prev => (
                prev.map(obj =>
                    obj._id === data.feedbackId
                    ? { ...obj, isClosed: data.status, updatedAt: data.feedbacks.updatedAt }
                    : obj
                )
            )
        );

        if(selectedFeedbackRef.current && selectedFeedbackRef.current._id === data.feedbackId){
            setSelectedFeedback(null);
            setFeedbackModal(false);
        }
    }, [setAllFeedback, setSelectedFeedback])

    const deleteSiteFeedback = async(feedbackId) => {
        try {
            const data = await SiteFeedbackAPI.deleteSiteFeedback(feedbackId);
            const feedbackUser = data.feedback.sender;
            if(data.message === 1){
                socket.emit('deletedFeedback', { feedbackUser, feedbackId, isAdmin });
                setAllFeedback(prev => (prev.filter(obj => obj._id !== feedbackId)));
                if(selectedFeedbackRef.current && selectedFeedbackRef.current._id === feedbackId){setSelectedFeedback(null); setFeedbackModal(false);}
            }
        } catch (error) {
            console.error("Error fetching site feedback:", error);
        }
    }

    const handleFeedbackDeleted = useCallback((data) => {
        setAllFeedback(prev => (prev.filter(obj => obj._id !== data.feedbackId)));
        if(selectedFeedbackRef.current && selectedFeedbackRef.current._id === data.feedbackId){setSelectedFeedback(null); setFeedbackModal(false)};
    }, [setAllFeedback, setSelectedFeedback]);

    useEffect(() => {
        if (isAdmin) {
            if(!(user.tier >= 2)){
                console.warn("Unauthorized admin access attempt");
                navigate('/');
            }
            setFeedbackAccess(() => getAllFeedback); 
            getAllFeedback();
        } else {
            setFeedbackAccess(() => getFeedbackUser); 
            getFeedbackUser();
        }
    }, [isAdmin, user, userId]);

    
    

    useEffect(() => {

        if (!socket) {
            return;
        }

        //emitUpdate('joinedAdminRoom');

        const eventHandlers = {
            'feedbackCreated': handleNewFeedback,
            'messageAdded': handleNewMessage, 
            'feedbackDeleted': handleFeedbackDeleted,
            'feedbackStatus': handleFeedbackStatus,
        };

        Object.entries(eventHandlers).forEach(([event, handler]) => {
            socket.on(event, handler);
        });

        return () => {
            
            Object.entries(eventHandlers).forEach(([event, handler]) => {
                socket.off(event, handler);
            });

            //emitUpdate('leaveAdminRoom');
        };
    }, [socket, isAdmin]);

    const sortedFeedback = [...allFeedback].sort((a, b) => 
        new Date(b.updatedAt) - new Date(a.updatedAt)
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };


    if(!user) return (<Loading/>)

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.heading}>Site Feedback</h2>
            </div>

            {!isAdmin && (
                <div className={styles.card}>
                    <div className={styles.inputGroup}>
                        <textarea
                            placeholder="Share your thoughts, report a bug, or suggest a feature..."
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className={styles.textArea}
                        />
                        <button onClick={handleSubmit} className={styles.submitBtn}>
                            Submit Feedback
                        </button>
                    </div>
                </div>
            )}

            <div className={styles.card} style={{ padding: 0, overflow: 'hidden' }}>
                {sortedFeedback.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No feedback submitted yet.</p>
                    </div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{width: '50px'}}>#</th>
                                    <th style={{width: '100px'}}>Status</th>
                                    <th>Feedback</th>
                                    <th>Sender</th>
                                    <th>Updated</th>
                                    <th style={{textAlign: 'right'}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedFeedback.map((feedback, index) => (
                                    <tr key={feedback._id}>
                                        <td className={styles.metaCell}>{index + 1}</td>
                                        <td>
                                            <span className={`${styles.badge} ${feedback.isClosed ? styles.badgeResolved : styles.badgeOpen}`}>
                                                {feedback.isClosed ? 'Resolved' : 'Open'}
                                            </span>
                                        </td>
                                        <td className={styles.textCell}>
                                            <div className={styles.truncate} title={feedback.text}>
                                                {feedback.text}
                                            </div>
                                        </td>
                                        <td>{feedback.sender?.username || 'Anonymous'}</td>
                                        <td className={styles.metaCell}>
                                            {formatDate(feedback.updatedAt)}
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button 
                                                    onClick={() => {
                                                        setSelectedFeedback(feedback);
                                                        setFeedbackModal(true);
                                                    }} 
                                                    className={styles.iconBtn}
                                                    title="View Details"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                                </button>

                                                {isAdmin && (
                                                    <>
                                                        {!feedback.isClosed ? (
                                                            <button 
                                                                onClick={() => statusSiteFeedback(feedback._id, true)} 
                                                                className={`${styles.iconBtn} ${styles.resolve}`}
                                                                title="Mark as Resolved"
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                            </button>
                                                        ) : (
                                                            <button 
                                                                onClick={() => statusSiteFeedback(feedback._id, false)} 
                                                                className={`${styles.iconBtn} ${styles.reopen}`}
                                                                title="Re-open"
                                                            >
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
                                                            </button>
                                                        )}

                                                        <button 
                                                            onClick={() => deleteSiteFeedback(feedback._id)} 
                                                            className={`${styles.iconBtn} ${styles.delete}`}
                                                            title="Delete"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {feedbackModal && (
                <SiteFeedbackModal
                    isOpen={feedbackModal}
                    onClose={() => setFeedbackModal(false)}
                    feedbackItem={selectedFeedback}
                    userId={userId}
                    setAllFeedback={setAllFeedback}
                    isAdmin={isAdmin}
                    setSelectedFeedback={setSelectedFeedback}
                />
            )}
        </div>
    );
};

export default SendSiteFeedback

// socket, notifications, live updates (use effects), delete method, 

//list of all the users (options to manually verify and unverify user accounts), ability to promote admins, 